// src/features/records/hooks/useRecordMark.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { mark as markRecord, unmark as unmarkRecord, subscribeMarked } from '../services/interactions/marks';

/**
 * Subscribe to a record mark for the current user and expose helpers.
 *
 * @param {string} recordId
 * @returns {{ marked: boolean, markedAt: any, mark: Function, unmark: Function, toggle: Function, loading: boolean, error: any }}
 */
const useRecordMark = (recordId) => {
  const { uid } = useAuth();

  const [marked, setMarked] = useState(false);
  const [markedAt, setMarkedAt] = useState(null);
  const [isHydrating, setIsHydrating] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState(null);

  const markedRef = useRef(marked);

  useEffect(() => {
    markedRef.current = marked;
  }, [marked]);

  useEffect(() => {
    if (!recordId || !uid) {
      setMarked(false);
      setMarkedAt(null);
      setIsHydrating(false);
      setError(null);
      return undefined;
    }

    setIsHydrating(true);
    setError(null);

    let unsubscribe = () => {};

    try {
      unsubscribe = subscribeMarked(
        recordId,
        ({ marked: nextMarked, markedAt: nextMarkedAt }) => {
          setMarked(Boolean(nextMarked));
          setMarkedAt(nextMarkedAt ?? null);
          setIsHydrating(false);
        },
        (err) => {
          console.error('Failed to subscribe to record mark:', err);
          setError(err);
          setIsHydrating(false);
        }
      );
    } catch (err) {
      console.error('Failed to start record mark subscription:', err);
      setError(err);
      setIsHydrating(false);
    }

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [recordId, uid]);

  const mark = useCallback(async () => {
    if (!recordId) {
      throw new Error('recordId is required');
    }

    const previous = markedRef.current;

    setError(null);
    setIsMutating(true);
    setMarked(true);

    try {
      await markRecord(recordId);
    } catch (err) {
      setMarked(previous);
      setError(err);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, [recordId]);

  const unmark = useCallback(async () => {
    if (!recordId) {
      throw new Error('recordId is required');
    }

    const previous = markedRef.current;

    setError(null);
    setIsMutating(true);
    setMarked(false);

    try {
      await unmarkRecord(recordId);
    } catch (err) {
      setMarked(previous);
      setError(err);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, [recordId]);

  const toggle = useCallback(async () => {
    if (markedRef.current) {
      return unmark();
    }
    return mark();
  }, [mark, unmark]);

  const loading = useMemo(() => isHydrating || isMutating, [isHydrating, isMutating]);

  return {
    marked,
    markedAt,
    mark,
    unmark,
    toggle,
    loading,
    error
  };
};

export default useRecordMark;
